import { Cpu } from 'lucide-react';
import { useToolModel, type ChatModel, type ToolModelSetting } from '@/hooks/useChatModel';
import { Button } from './ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';

/**
 * Which model a whole tool drafts with.
 *
 * One choice per tool, not per session: a drafter decides how their applications get
 * written once, and every step of every application follows it. That is why this sits in
 * a toolbar rather than on a step — a per-step picker would invite a specification whose
 * background was written by one model and whose claims by another, which reads exactly
 * as badly as it sounds.
 *
 * The saved value goes to the account's settings, so the backend can read it when it
 * queues work that no page is watching.
 */
export function ToolModelPicker({
  setting,
  label,
  compact = false,
}: {
  setting: ToolModelSetting;
  /** What this model is used for, e.g. "Drafting model". */
  label: string;
  /** Icon and value only — for a crowded toolbar. */
  compact?: boolean;
}) {
  const { model, setModel, label: modelLabel, options } = useToolModel(setting);

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className="h-8 gap-1.5 px-2 text-xs text-slate-500 hover:text-slate-900"
          aria-label={`${label}: ${modelLabel}`}
          title={`${label} — used for every session in this tool. Currently ${modelLabel}.`}
        >
          <Cpu className="h-4 w-4 shrink-0" />
          <span className="max-w-28 truncate">{modelLabel}</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-72">
        <DropdownMenuLabel className="font-normal">
          <span className="block text-xs font-medium text-slate-900">{label}</span>
          <span className="block text-[11px] text-slate-500">
            {compact ? 'Used for every session.' : 'Used for every session in this tool.'}
          </span>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuRadioGroup
          value={model}
          onValueChange={value => setModel(value as ChatModel)}
        >
          {options.map(option => (
            <DropdownMenuRadioItem
              key={option.value}
              value={option.value}
              disabled={option.disabled}
            >
              <div>
                <div className="flex items-center gap-1.5">
                  {option.label}
                  <span className="text-[10px] uppercase tracking-wide text-slate-400">
                    {option.provider === 'anthropic' ? 'Anthropic' : 'OpenAI'}
                  </span>
                </div>
                <p className="text-xs text-slate-500">{option.description}</p>
                {option.requestNote && (
                  <p className="text-xs text-amber-700">{option.requestNote}</p>
                )}
              </div>
            </DropdownMenuRadioItem>
          ))}
        </DropdownMenuRadioGroup>
        <DropdownMenuSeparator />
        <p className="px-2 py-1.5 text-[11px] leading-relaxed text-slate-500">
          Reference numerals and the parts list are always generated by a fixed model —
          their output feeds the numeral allocator, so a swap there would renumber a
          drawing.
        </p>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export default ToolModelPicker;
